/**
 * The admin panel's domain models — camelCase, projected from the snake_case wire shapes in
 * `backend-types/admin-api.ts`. The projection itself lives in `domain/adminMappers.ts`; this file
 * is types only, so components can import a model without pulling the wire contract in with it.
 *
 * The status/plan unions are re-exported under domain names (`AdminSubscriptionPlan`, `EmailStatus`
 * …) rather than components importing the backend file directly. That keeps a re-copy of the wire
 * types from rippling through the UI, and it avoids the collision between the backend's
 * `SubscriptionPlan` (`free | pro | team`) and this app's own (`pro_monthly | pro_yearly`).
 *
 * The one thing to keep straight throughout: **the raw Paddle mirror and the derived truth are
 * different fields and mean different things.** `subscribed` / `subscriptionPlan` /
 * `subscriptionExpiresAt` lag reality; `isSubscriptionActive` / `effectiveSubscriptionPlan` do not.
 * A lapsed Pro reads `subscriptionPlan: 'pro'` and `effectiveSubscriptionPlan: 'free'`, and badging
 * off the first one is the specific bug this two-field contract exists to prevent.
 */

import type {
  AdminEmailStatus,
  AdminEmailType,
  AdminPendingSignup,
  AdminTranscriptionStatus,
  AdminUserSort,
} from '../backend-types/admin-api';
import type {SubscriptionPlan, VideoEditor} from '../backend-types/user';

export type AdminSubscriptionPlan = SubscriptionPlan;
export type EmailStatus = AdminEmailStatus;
export type EmailType = AdminEmailType;
export type EmailTrigger = 'system' | 'user' | 'admin';
export type SignupStatus = AdminPendingSignup['status'];
export type {AdminTranscriptionStatus};

/* ==========================================================================
 * Dashboard
 * ========================================================================== */

export interface AdminDashboardStats {
  usersTotal: number;
  subscribersActive: number;
  subscribersPaid: number;
  usersNew30d: number;
  transcriptionsInFlight: number;
  signupsOpen: number;
  /** Paid, no account. Anything above zero is someone who has been charged and cannot sign in. */
  signupsFailed: number;
  /** 30 days of individual sends that did not arrive. */
  emailsUndelivered30d: number;
  /** The standing blocklist behind those failures — addresses Resend refuses outright. */
  suppressionsActive: number;
}

/* ==========================================================================
 * Users
 * ========================================================================== */

export interface AdminUser {
  /** `public.users.id`. Every admin route keys off this, never `supabaseUserId`. */
  id: string;
  supabaseUserId: string | null;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
  passwordSet: boolean;
  welcomePage: boolean;

  /** Raw Paddle mirror. Lags. Shown only on the detail page, labelled as the mirror. */
  subscribed: boolean;
  subscriptionPlan: AdminSubscriptionPlan;
  subscriptionExpiresAt: string | null;

  /** Derived server-side at query time. Badge off these. */
  isSubscriptionActive: boolean;
  effectiveSubscriptionPlan: AdminSubscriptionPlan;

  paddleCustomerId: string | null;
  paddleSubscriptionId: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;

  /** Excludes soft-deleted rows — what the user can still see. */
  transcriptionCount: number;
  /**
   * Lifetime billable seconds, a whole number. Soft-deleted transcriptions **are** counted here
   * even though `transcriptionCount` excludes them: the row is the ledger entry for the seconds it
   * spent, and deleting is not a refund.
   */
  transcriptionSecondsTotal: number;
  transcriptionsInFlight: number;
  transcriptionsFailed: number;
  lastTranscriptionAt: string | null;
  conversionCount: number;
  lastConversionAt: string | null;
  projectCount: number;
  audioUploadCount: number;
}

/** Every list endpoint pages the same way, so the page envelope is one generic. */
export interface AdminPage<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/** Embedded owner on the global lists, so a row is actionable without a second call. */
export interface AdminUserRef {
  id: string;
  email: string;
  name: string | null;
}

/* ==========================================================================
 * User detail
 * ========================================================================== */

export interface AdminUserIdentity {
  id: string;
  supabaseUserId: string | null;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
  passwordSet: boolean;
  welcomePage: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserSubscription {
  subscribed: boolean;
  subscriptionPlan: AdminSubscriptionPlan;
  subscriptionExpiresAt: string | null;
  isSubscriptionActive: boolean;
  effectiveSubscriptionPlan: AdminSubscriptionPlan;
  paddleCustomerId: string | null;
  paddleSubscriptionId: string | null;
  lastSyncedAt: string | null;
}

/**
 * Null on the detail means the plan limits failed to load: render "unavailable", never zero.
 *
 * Produced by the same `getTranscriptionUsage()` the app's own `/usage` and the 402s go through, so
 * an admin and the customer can never be quoted different numbers.
 */
export interface AdminUserUsage {
  plan: AdminSubscriptionPlan;
  secondsUsed: number;
  secondsLimit: number | null;
  secondsRemaining: number | null;
  /** The user's own billing period — anchored on their renewal or signup date, not the 1st. */
  periodStart: string;
  periodEnd: string;
  maxFileSeconds: number | null;
}

export interface AdminUserLifetime {
  transcriptionCount: number;
  transcriptionSecondsTotal: number;
  transcriptionsInFlight: number;
  transcriptionsFailed: number;
  conversionCount: number;
  audioUploadCount: number;
  projectCount: number;
  lastTranscriptionAt: string | null;
  lastConversionAt: string | null;
}

export interface AdminSignup {
  id: string;
  email: string;
  name: string | null;
  status: SignupStatus;
  paddleCustomerId: string | null;
  paddleSubscriptionId: string | null;
  /** `public.users.id` — safe to link straight to the detail page. Null until provisioned. */
  userId: string | null;
  lastError: string | null;
  loginLinkSentAt: string | null;
  provisionedAt: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface AdminEmail {
  id: string;
  emailType: EmailType;
  /** The address we sent to at the time, which may differ from the user's today. */
  recipient: string;
  status: EmailStatus;
  error: string | null;
  triggeredBy: EmailTrigger;
  triggeredByUserId: string | null;
  providerMessageId: string | null;
  /** When the provider says the event happened, not when we wrote the row. */
  providerEventAt: string | null;
  createdAt: string;
  /** Null for a send that happened before provisioning created the account. */
  userId: string | null;
  /** Global list only. Null when `userId` is — fall back to `recipient`. */
  user: AdminUserRef | null;
}

/** Null means "no record of a setup email", which is not the same as "none was sent". */
export interface AdminSetupEmail {
  status: EmailStatus;
  sentAt: string;
  deliveredAt: string | null;
  error: string | null;
  triggeredBy: EmailTrigger;
  sendCount: number;
}

/**
 * An address Resend refuses to deliver to. Added automatically after a hard bounce or a spam
 * complaint, or manually in the Resend dashboard, and cleared only there — this API mirrors the
 * state and cannot change it.
 *
 * Non-null on a user detail means every send to them is dropped, which is why it belongs beside the
 * resend button rather than in a timeline.
 */
export interface AdminSuppression {
  id: string | null;
  email: string;
  origin: 'bounce' | 'complaint' | 'manual' | null;
  sourceEmailId: string | null;
  suppressedAt: string;
  /** Null = still suppressed. Set on removal rather than the row being deleted. */
  removedAt: string | null;
  createdAt: string | null;
}

export interface AdminTranscription {
  id: string;
  name: string;
  audioFilename: string | null;
  status: AdminTranscriptionStatus;
  durationSeconds: number | null;
  size: number | null;
  /** Soft-deleted rows are in the list on purpose — they still spent their seconds. */
  isDeleted: boolean;
  error: string | null;
  projectId: string | null;
  assemblyaiTranscriptId: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** Global queue only. */
  userId: string | null;
  user: AdminUserRef | null;
}

export interface AdminConversion {
  id: string;
  transcriptionId: string | null;
  projectId: string | null;
  mode: string;
  markers: string | null;
  censoredWords: number | null;
  totalCuts: number | null;
  wordListName: string | null;
  csvR2Key: string | null;
  xmlR2Key: string | null;
  /**
   * The media reference the XML's source clip was built with. Null on a row from before
   * 2026-08-26 just means it predates the column; null on a newer row is unusual and is what
   * hangs DaVinci Resolve on import — pair it with videoEditor.
   */
  audioPath: string | null;
  /** Only meaningful when mode is 'beep'. Null otherwise, or on rows that predate this column. */
  beepSoundPath: string | null;
  /** A SNAPSHOT of the editor at generation time, not the user's current profile setting. */
  videoEditor: VideoEditor | null;
  /** The frame rate the XML's timebase was generated at. Null predates 2026-08-29. */
  fps: number | null;
  createdAt: string;
}

export interface AdminProjectSummary {
  id: string;
  name: string;
  isDefault: boolean;
}

export interface AdminUserProfile {
  user: AdminUserIdentity;
  subscription: AdminUserSubscription;
  usage: AdminUserUsage | null;
  lifetime: AdminUserLifetime;
  signup: AdminSignup | null;
  setupEmail: AdminSetupEmail | null;
  /** Non-null ⇒ every send to this address is being dropped. */
  emailSuppression: AdminSuppression | null;
  recentEmails: AdminEmail[];
  recentTranscriptions: AdminTranscription[];
  recentConversions: AdminConversion[];
  projects: AdminProjectSummary[];
}

/* ==========================================================================
 * Query parameters
 * ========================================================================== */

export type AdminUserSortField = AdminUserSort;

export type AdminSortOrder = 'asc' | 'desc';

/** Filters the **derived** `is_subscription_active`, matching the badge beside it. */
export type AdminUserStatusFilter = 'active' | 'inactive';

export interface AdminUserQuery {
  /** Case-insensitive substring against email or name. Safe to pass a full address. */
  search?: string;
  status?: AdminUserStatusFilter;
  /** Filters the **derived** effective plan, not the raw column. */
  plan?: AdminSubscriptionPlan;
  /** Only ever sent as `false` — the endpoint's filter is `password_set=false`. */
  passwordSetFalse?: boolean;
  isAdmin?: boolean;
  /** ISO timestamp, inclusive. */
  createdAfter?: string;
  /** ISO timestamp, exclusive. */
  createdBefore?: string;
  /** Only sent as `true` — restricts to users with at least one job running right now. */
  inFlight?: boolean;
  sort?: AdminUserSortField;
  order?: AdminSortOrder;
  limit?: number;
  offset?: number;
}

export interface AdminSignupQuery {
  status?: SignupStatus;
  /** Case-insensitive substring against the signup email or name. */
  search?: string;
  limit?: number;
  offset?: number;
}

/** `undelivered` is the server's shorthand for send_failed,bounced,complained,suppressed. */
export type AdminEmailStatusFilter = EmailStatus | 'undelivered';

export interface AdminEmailQuery {
  status?: AdminEmailStatusFilter;
  emailType?: EmailType;
  /** ISO timestamp, inclusive. */
  since?: string;
  /** Case-insensitive substring against the recipient address. */
  search?: string;
  order?: AdminSortOrder;
  limit?: number;
  offset?: number;
}

export interface AdminSuppressionQuery {
  /** Only sent as `true` — the default is active suppressions only. */
  includeRemoved?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AdminTranscriptionQueueQuery {
  status?: AdminTranscriptionStatus;
  since?: string;
  /** The endpoint defaults to `asc`: in a set of running jobs, the oldest is the stuck one. */
  order?: AdminSortOrder;
  limit?: number;
  offset?: number;
}

/** Default page size for the user table. The endpoint's own default is 50, its cap is 200. */
export const ADMIN_USERS_PAGE_SIZE = 50;

/** Default page size for the per-user activity tabs. The endpoint's own default is 25. */
export const ADMIN_ACTIVITY_PAGE_SIZE = 25;

export const ADMIN_SIGNUPS_PAGE_SIZE = 50;

export const ADMIN_EMAILS_PAGE_SIZE = 50;

export const ADMIN_QUEUE_PAGE_SIZE = 50;
